import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { Calendar, ChevronUp } from "lucide-react";

/* ─── Custom mini calendar (all inline styles for portal compat) ─── */
const MiniCalendar = ({ value, onSelect, t }) => {
  const today = new Date();
  const selected = value ? new Date(value + "T00:00:00") : null;
  const [viewYear, setViewYear] = useState(
    selected ? selected.getFullYear() : today.getFullYear(),
  );
  const [viewMonth, setViewMonth] = useState(
    selected ? selected.getMonth() : today.getMonth(),
  );

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const monthLabel = new Date(viewYear, viewMonth).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const prev = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else setViewMonth(viewMonth - 1);
  };
  const next = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else setViewMonth(viewMonth + 1);
  };

  const fmt = (y, m, d) =>
    `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const isToday = (d) =>
    today.getFullYear() === viewYear &&
    today.getMonth() === viewMonth &&
    today.getDate() === d;
  const isSelected = (d) =>
    selected &&
    selected.getFullYear() === viewYear &&
    selected.getMonth() === viewMonth &&
    selected.getDate() === d;

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const gridS = {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 0,
  };
  const cellS = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  const navBtnS = {
    padding: 4,
    borderRadius: 4,
    background: "transparent",
    border: "none",
    color: t.textSecondary,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  };

  return (
    <div>
      {/* Month nav */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 4px",
          marginBottom: 8,
        }}
      >
        <button
          style={navBtnS}
          onClick={prev}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = t.hoverBg;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
        >
          <ChevronUp size={14} style={{ transform: "rotate(-90deg)" }} />
        </button>
        <span style={{ fontSize: 12, fontWeight: 500, color: t.textPrimary }}>
          {monthLabel}
        </span>
        <button
          style={navBtnS}
          onClick={next}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = t.hoverBg;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
        >
          <ChevronUp size={14} style={{ transform: "rotate(90deg)" }} />
        </button>
      </div>
      {/* Day headers */}
      <div style={{ ...gridS, marginBottom: 4 }}>
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <div
            key={d}
            style={{
              textAlign: "center",
              fontSize: 10,
              fontWeight: 500,
              padding: "2px 0",
              color: t.textMuted,
            }}
          >
            {d}
          </div>
        ))}
      </div>
      {/* Day grid */}
      <div style={gridS}>
        {days.map((d, i) => (
          <div key={i} style={cellS}>
            {d ? (
              <button
                onClick={() => onSelect(fmt(viewYear, viewMonth, d))}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  fontSize: 11,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  background: isSelected(d) ? t.activeBg : "transparent",
                  border: isSelected(d)
                    ? `1.5px solid ${t.activeBorder}`
                    : isToday(d)
                      ? `1.5px solid ${t.divider}`
                      : "1.5px solid transparent",
                  color: isSelected(d) ? t.activeText : t.textSecondary,
                  fontWeight: isSelected(d) || isToday(d) ? 600 : 400,
                  transition: "all 0.12s ease",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected(d)) {
                    e.currentTarget.style.background = t.hoverBg;
                    e.currentTarget.style.borderColor = t.hoverBorder;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected(d)) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.borderColor = isToday(d)
                      ? t.divider
                      : "transparent";
                  }
                }}
              >
                {d}
              </button>
            ) : (
              <div style={{ width: 32, height: 32 }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Date range picker (fully custom, portal dropdown) ─── */
/**
 * Universal DateRangePicker component.
 *
 * Props:
 *   dateFrom  - "YYYY-MM-DD" string or ""
 *   dateTo    - "YYYY-MM-DD" string or ""
 *   onChange  - (from, to) => void
 *   t         - flat theme object with keys: textPrimary, textSecondary, textMuted,
 *               inputBg, inputBorder, activeBg, activeBorder, activeText,
 *               hoverBg, hoverBorder, hoverBtnShadow, divider,
 *               dropBg, dropBorder, dropShadow, pillBg, pillBorder,
 *               clearText, clearBg, clearBgHover, clearBorder
 *   compact   - boolean, if true renders a smaller trigger (for graph control bar)
 */
const DateRangePicker = ({
  dateFrom,
  dateTo,
  onChange,
  t,
  compact = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [picking, setPicking] = useState(null); // "from" | "to" | null
  const triggerRef = useRef(null);
  const dropRef = useRef(null);
  const hasRange = dateFrom || dateTo;

  /* ── Position the dropdown via direct DOM ── */
  const positionDrop = useCallback(() => {
    if (!triggerRef.current || !dropRef.current) return;
    const tr = triggerRef.current.getBoundingClientRect();
    const dr = dropRef.current;
    const dropW = 280;
    let top = tr.bottom + 6;
    let left = tr.left;
    if (left + dropW > window.innerWidth - 10)
      left = window.innerWidth - dropW - 10;
    if (left < 10) left = 10;
    const dropH = dr.offsetHeight || 380;
    if (top + dropH > window.innerHeight - 10) top = tr.top - dropH - 6;
    if (top < 10) top = 10;
    dr.style.top = `${top}px`;
    dr.style.left = `${left}px`;
    dr.style.width = `${dropW}px`;
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    positionDrop();
  }, [isOpen, picking, positionDrop]);

  useEffect(() => {
    if (!isOpen) return;
    const repos = () => positionDrop();
    window.addEventListener("scroll", repos, true);
    window.addEventListener("resize", repos);
    return () => {
      window.removeEventListener("scroll", repos, true);
      window.removeEventListener("resize", repos);
    };
  }, [isOpen, positionDrop]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        dropRef.current &&
        !dropRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setPicking(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  const presets = [
    { label: "Today", from: 0 },
    { label: "7 days", from: 7 },
    { label: "30 days", from: 30 },
    { label: "90 days", from: 90 },
  ];

  const applyPreset = (daysBack) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - daysBack);
    const fmt = (d) => d.toISOString().split("T")[0];
    onChange(fmt(from), fmt(to));
    setIsOpen(false);
    setPicking(null);
  };

  const clearDates = () => {
    onChange("", "");
    setIsOpen(false);
    setPicking(null);
  };

  const displayLabel = () => {
    if (!dateFrom && !dateTo) return "Date Range";
    if (dateFrom && dateTo) {
      const f = new Date(dateFrom + "T00:00:00");
      const t2 = new Date(dateTo + "T00:00:00");
      const fmt = (d) =>
        d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      return `${fmt(f)} – ${fmt(t2)}`;
    }
    if (dateFrom)
      return `From ${new Date(dateFrom + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
    return `Until ${new Date(dateTo + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  };

  const fmtChip = (val) => {
    if (!val) return "Any";
    return new Date(val + "T00:00:00").toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const presetBtnS = {
    padding: "6px 10px",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.15s ease",
  };
  const chipBtnS = (active) => ({
    flex: 1,
    padding: "8px 10px",
    borderRadius: 8,
    fontSize: 12,
    textAlign: "left",
    cursor: "pointer",
    transition: "all 0.15s ease",
    background: active ? t.activeBg : t.inputBg,
    border: active
      ? `1px solid ${t.activeBorder}`
      : `1px solid ${t.inputBorder}`,
    color: active ? t.activeText : t.textSecondary,
  });
  const dividerS = {
    margin: "0 10px",
    height: 1,
    background: t.divider,
    opacity: 0.4,
  };

  const dropdown = isOpen
    ? createPortal(
        <div
          ref={dropRef}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 99999,
            background: t.dropBg,
            border: `1px solid ${t.dropBorder}`,
            boxShadow: t.dropShadow,
            borderRadius: 12,
            overflow: "hidden",
            fontFamily: "inherit",
          }}
        >
          {/* Presets */}
          <div
            style={{ padding: 10, display: "flex", flexWrap: "wrap", gap: 6 }}
          >
            {presets.map((p) => (
              <button
                key={p.label}
                onClick={() => applyPreset(p.from)}
                style={{
                  ...presetBtnS,
                  background: t.pillBg,
                  border: `1px solid ${t.pillBorder}`,
                  color: t.textSecondary,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = t.hoverBg;
                  e.currentTarget.style.borderColor = t.hoverBorder;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = t.pillBg;
                  e.currentTarget.style.borderColor = t.pillBorder;
                }}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div style={dividerS} />

          {/* From / To selector chips */}
          <div
            style={{
              padding: 10,
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <button
              onClick={() => setPicking("from")}
              style={chipBtnS(picking === "from")}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  marginBottom: 2,
                  color: t.textMuted,
                }}
              >
                From
              </div>
              <div style={{ fontWeight: 500 }}>{fmtChip(dateFrom)}</div>
            </button>
            <span style={{ color: t.textMuted, fontSize: 12 }}>–</span>
            <button
              onClick={() => setPicking("to")}
              style={chipBtnS(picking === "to")}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  marginBottom: 2,
                  color: t.textMuted,
                }}
              >
                To
              </div>
              <div style={{ fontWeight: 500 }}>{fmtChip(dateTo)}</div>
            </button>
          </div>

          {/* Calendar */}
          {picking && (
            <>
              <div style={dividerS} />
              <div style={{ padding: 10 }}>
                <MiniCalendar
                  value={picking === "from" ? dateFrom : dateTo}
                  onSelect={(val) => {
                    if (picking === "from") {
                      onChange(val, dateTo);
                      setPicking("to");
                    } else {
                      onChange(dateFrom, val);
                      setPicking(null);
                    }
                  }}
                  t={t}
                />
              </div>
            </>
          )}

          {hasRange && (
            <div style={{ padding: "0 10px 10px" }}>
              <button
                onClick={clearDates}
                style={{
                  width: "100%",
                  textAlign: "center",
                  padding: "6px 0",
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: "pointer",
                  color: t.clearText,
                  background: t.clearBg,
                  border: `1px solid ${t.clearBorder}`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = t.clearBgHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = t.clearBg;
                }}
              >
                Clear dates
              </button>
            </div>
          )}
        </div>,
        document.body,
      )
    : null;

  return (
    <div ref={triggerRef} className="relative">
      <button
        onClick={() => {
          setIsOpen((v) => !v);
          setPicking(null);
        }}
        className={`flex items-center gap-2 rounded-lg transition-all duration-200 ${compact ? "px-2 py-1 text-xs" : "px-3 py-2 text-sm"}`}
        style={{
          background: hasRange ? t.activeBg : t.inputBg,
          border: hasRange
            ? `1px solid ${t.activeBorder}`
            : `1px solid ${t.inputBorder}`,
          color: hasRange ? t.activeText : t.textSecondary,
          cursor: "pointer",
          minWidth: compact ? 100 : 120,
        }}
        onMouseEnter={(e) => {
          if (!hasRange) {
            e.currentTarget.style.border = `1px solid ${t.hoverBorder}`;
            e.currentTarget.style.boxShadow = t.hoverBtnShadow;
          }
        }}
        onMouseLeave={(e) => {
          if (!hasRange) {
            e.currentTarget.style.border = `1px solid ${t.inputBorder}`;
            e.currentTarget.style.boxShadow = "none";
          }
        }}
      >
        <Calendar
          size={compact ? 12 : 14}
          style={{ opacity: 0.6, flexShrink: 0 }}
        />
        <span
          className="truncate flex-1 text-left"
          style={{ fontSize: compact ? 11 : 12 }}
        >
          {displayLabel()}
        </span>
        <ChevronUp
          size={compact ? 12 : 14}
          style={{
            opacity: 0.4,
            flexShrink: 0,
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s ease",
          }}
        />
      </button>
      {dropdown}
    </div>
  );
};

export default DateRangePicker;
